import React from "react"

interface FilesContextValue {
  state: File[]
  dispatch: React.Dispatch<FilesAction>
}

type FilesAction =
  | { type: "ADD_FILES"; files: File[] }
  | { type: "REMOVE_FILE"; index: number }
  | { type: "CLEAR_FILES" }

const FilesContext = React.createContext<FilesContextValue | undefined>(
  undefined
)
const ADD_FILES = "ADD_FILES"
const REMOVE_FILE = "REMOVE_FILE"
const CLEAR_FILES = "CLEAR_FILES"

const filesReducer = (state: File[], action: FilesAction): File[] => {
  switch (action.type) {
    case ADD_FILES: {
      return [...state, ...action.files]
    }
    case REMOVE_FILE: {
      return state.filter((file, i) => i !== action.index)
    }
    case CLEAR_FILES: {
      return []
    }

    default:
      throw new Error(`Action is not supported: ${(action as any).type}`)
  }
}

const initialState: File[] = []

export const FilesProvider: React.FC<{
  children?: React.ReactNode
}> = props => {
  const [state, dispatch] = React.useReducer(filesReducer, initialState)

  const value = React.useMemo(() => ({ state, dispatch }), [state])

  return <FilesContext.Provider value={value} {...props} />
}

interface UseFilesContextReturn {
  files: File[]
  addFiles: (files: File[]) => void
  removeFile: (index: number) => void
  clearFiles: () => void
}

export const useFilesContext = (): UseFilesContextReturn => {
  const context = React.useContext(FilesContext)

  if (!context) {
    throw new Error("useFilesContext must be used inside a FilesProvider")
  }

  const { state, dispatch } = context

  const addFiles = (files: File[]) => {
    dispatch({ type: ADD_FILES, files: files })
  }

  const removeFile = (index: number) => {
    dispatch({ type: REMOVE_FILE, index: index })
  }

  const clearFiles = () => {
    dispatch({ type: CLEAR_FILES })
  }

  const files = state

  return { files, addFiles, removeFile, clearFiles }
}
